// A PCBA's board components (Mitch, 2026-11-01: "make it clear exactly what components are consigned, and which have to
// be purchased by our CM … crystal clear"): read from the board's KiCad schematics themselves, never an exported BOM
// ("just pull the BOM from the schematic file … the formatting is not guaranteed there"). Each component row is either
// sent by HHL (a BOM line with that part, its references in Ref) or bought by the CM (the default: no part needed,
// covered by the maker's Components charge). Read on the server from the spec's "<board> KiCad project.zip"
// (server/src/board.ts), shown on the BOM editor's Board tab (web/src/pages/BoardTab.tsx), and given to the CM with a
// "Supplied by" column (boardBomCsv()).

export type BoardRow = {
  /** The row's references in order ("C1", "C2"), its key. */
  refs: string[];
  qty: number;
  value: string;
  /** The footprint's name, without its library ("R_0402_1005Metric"). */
  footprint: string;
  /** A manufacturer (or LCSC) part number, when the symbols have a field for it. */
  mpn: string;
  manufacturer: string;
  description: string;
  /** KiCad's "Not on board" (a battery on wires): still on the BOM, not soldered on. */
  offBoard: boolean;
};

export type BoardBom = {
  rows: BoardRow[];
  /** Components marked Do not populate, and ones excluded from the BOM (each reference). */
  dnp: string[];
  notInBom: string[];
  /** Sheets read, the root one included. */
  sheets: number;
  warnings: string[];
};

// --- KiCad's S-expressions ---

type Node = (string | Node)[];

/** A schematic's S-expression: lists of "strings" (\" \\ \n escapes) and bare atoms, each atom a string. */
export function parseSexp(text: string): Node {
  const root: Node = [];
  const stack: Node[] = [root];
  const n = text.length;
  let i = 0;
  while (i < n) {
    const c = text.charCodeAt(i);
    if (c === 40) {
      const list: Node = [];
      stack[stack.length - 1].push(list);
      stack.push(list);
      i++;
    } else if (c === 41) {
      if (stack.length > 1) stack.pop();
      i++;
    } else if (c <= 32) i++;
    else if (c === 34) {
      let s = "";
      let from = ++i;
      while (i < n && text.charCodeAt(i) !== 34) {
        if (text.charCodeAt(i) === 92) {
          const e = text[i + 1] ?? "";
          s += text.slice(from, i) + (e === "n" ? "\n" : e === "t" ? "\t" : e);
          i += 2;
          from = i;
        } else i++;
      }
      stack[stack.length - 1].push(s + text.slice(from, i));
      i++;
    } else {
      const from = i;
      while (i < n) {
        const d = text.charCodeAt(i);
        if (d <= 32 || d === 40 || d === 41) break;
        i++;
      }
      stack[stack.length - 1].push(text.slice(from, i));
    }
  }
  return (root.find(Array.isArray) as Node | undefined) ?? [];
}

const lists = (n: Node | undefined, name: string) => (n ?? []).filter((c): c is Node => Array.isArray(c) && c[0] === name);
const first = (n: Node | undefined, name: string) => lists(n, name)[0];
const str = (n: Node | undefined, i = 1) => (n && typeof n[i] === "string" ? (n[i] as string) : "");
const flag = (n: Node, name: string, otherwise: boolean) => {
  const f = first(n, name);
  return f ? str(f) === "yes" : otherwise;
};
const fields = (n: Node) => new Map(lists(n, "property").map((p) => [str(p, 1), str(p, 2)] as const));
const blank = (s: string | undefined) => !s || s === "~";

const base = (path: string) => path.split("/").pop() ?? path;
const dir = (path: string) => path.split("/").slice(0, -1);
/** "a/./b/../c.kicad_sch" → "a/c.kicad_sch". */
function norm(parts: readonly string[]) {
  const out: string[] = [];
  for (const p of parts) {
    if (p === "..") out.pop();
    else if (p && p !== ".") out.push(p);
  }
  return out.join("/");
}

const MPN = [
  /^(mpn|manufacturer[ _]?part[ _]?(number|no\.?|#)?|mfr\.?[ _]?(part[ _]?(number|no\.?|#)?|p\/?n|no\.?|#)|mfg[ _]?(part[ _]?(number|#)?|p\/?n)|part[ _]?(number|no\.?|#)|p\/n)$/i,
  /^(lcsc|lcsc[ _]?(part|pn)[ _]?(#|number)?|jlc(pcb)?[ _]?part[ _]?#?)$/i,
];
const MAKER = /^(manufacturer|mfr|mfg|manufacturer[ _]?name)$/i;
const DNP_FIELD = /^(dnp|do not (populate|place))$/i;
const fieldOf = (f: Map<string, string>, re: RegExp) => [...f].find(([k, v]) => re.test(k.trim()) && !blank(v))?.[1]?.trim() ?? "";

/** References in board order: by letters, then number ("R2" before "R10"). */
export const refOrder = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

type Part = { ref: string; lib: string; value: string; footprint: string; mpn: string; manufacturer: string; description: string; offBoard: boolean };

/**
 * The board's components from its project's schematics (path in the project → text): the root sheet (named after the
 * board, else the one no sheet uses) and every sheet under it, a sheet used twice read twice with each use's own
 * references (KiCad 7+'s instances, KiCad 6's symbol_instances). Power symbols and flags (#PWR, #FLG) aren't parts;
 * a multi-unit part (U1A, U1B) is one; Do not populate and Exclude from BOM ones are listed apart, and so are a sheet's
 * when the sheet says so. Rows group the same value, footprint and part number, like KiCad's own BOM.
 */
export function boardFromSchematics(files: ReadonlyMap<string, string>, board = ""): BoardBom {
  const paths = [...files.keys()].map((p) => p.replace(/\\/g, "/")).filter((p) => /\.kicad_sch$/i.test(p));
  const text = new Map([...files].map(([p, t]) => [p.replace(/\\/g, "/"), t] as const));
  const out: BoardBom = { rows: [], dnp: [], notInBom: [], sheets: 0, warnings: [] };
  if (!paths.length) return { ...out, warnings: ["The project has no schematic (.kicad_sch)"] };

  const stem = (p: string) =>
    base(p)
      .replace(/\.kicad_sch$/i, "")
      .toLowerCase();
  const used = new Set<string>();
  for (const t of text.values()) for (const m of t.matchAll(/\(property\s+"Sheet ?file"\s+"([^"]+)"/gi)) used.add(base(m[1].replace(/\\/g, "/")).toLowerCase());
  const byLength = (a: string, b: string) => a.split("/").length - b.split("/").length || a.localeCompare(b);
  const named = paths.filter((p) => board && stem(p) === board.toLowerCase()).sort(byLength);
  const tops = paths.filter((p) => !used.has(base(p).toLowerCase())).sort(byLength);
  const root = named[0] ?? tops[0] ?? [...paths].sort(byLength)[0];
  if (!named.length && tops.length > 1) out.warnings.push(`More than one top sheet (${tops.map(base).join(", ")}): read ${base(root)}`);

  const trees = new Map<string, Node>();
  const tree = (p: string) => {
    let t = trees.get(p);
    if (!t) trees.set(p, (t = parseSexp(text.get(p) ?? "")));
    return t;
  };
  const resolve = (from: string, file: string) => {
    const f = file.replace(/\\/g, "/");
    const want = [norm([...dir(from), ...f.split("/")]), norm([...dir(root), ...f.split("/")])].map((w) => w.toLowerCase());
    return (
      want.map((w) => paths.find((p) => p.toLowerCase() === w)).find(Boolean) ?? paths.find((p) => base(p).toLowerCase() === base(f).toLowerCase()) ?? null
    );
  };

  // KiCad 6 keeps every placed symbol's reference in the root sheet, by its path without the root ("/sheet/symbol").
  const legacy = new Map<string, Node>();
  for (const p of lists(first(tree(root), "symbol_instances"), "path")) legacy.set(str(p), p);

  const parts = new Map<string, Part>();
  const dnp = new Set<string>();
  const notInBom = new Set<string>();
  const unannotated = new Set<string>();
  const twice = new Set<string>();

  const walk = (file: string, path: string, legacyPath: string, sheet: { dnp: boolean; inBom: boolean }, depth: number) => {
    if (depth > 24) {
      out.warnings.push(`${base(file)} uses itself: its sheets were read 24 deep`);
      return;
    }
    out.sheets++;
    const t = tree(file);
    for (const sym of lists(t, "symbol")) {
      const lib = str(first(sym, "lib_id"));
      if (!lib) continue;
      const uuid = str(first(sym, "uuid"));
      const f = fields(sym);
      const instance = lists(first(sym, "instances"), "project")
        .flatMap((p) => lists(p, "path"))
        .find((p) => str(p) === path);
      const old = legacy.get(`${legacyPath}/${uuid}`);
      const ref = (str(first(instance, "reference")) || str(first(old, "reference")) || f.get("Reference") || "").trim();
      if (!ref || ref.startsWith("#") || /^power:/i.test(lib)) continue;
      if (ref.endsWith("?")) {
        unannotated.add(ref);
        continue;
      }
      const value = (str(first(old, "value")) || f.get("Value") || "").trim();
      const fp = (str(first(old, "footprint")) || f.get("Footprint") || "").trim();
      const seen = parts.get(ref);
      if (seen || dnp.has(ref) || notInBom.has(ref)) {
        // Another unit of a multi-unit part, or a reference used twice.
        if (seen && (seen.lib !== lib || seen.value !== value)) twice.add(ref);
        continue;
      }
      const dnpField = f.get([...f.keys()].find((k) => DNP_FIELD.test(k.trim())) ?? "");
      if (sheet.dnp || flag(sym, "dnp", false) || (!blank(dnpField) && !/^(no|n|0|false)$/i.test(dnpField?.trim() ?? ""))) {
        dnp.add(ref);
        continue;
      }
      if (!sheet.inBom || !flag(sym, "in_bom", true)) {
        notInBom.add(ref);
        continue;
      }
      parts.set(ref, {
        ref,
        lib,
        value: blank(value) ? "" : value,
        footprint: blank(fp) ? "" : fp.slice(fp.indexOf(":") + 1),
        mpn: fieldOf(f, MPN[0]) || fieldOf(f, MPN[1]),
        manufacturer: fieldOf(f, MAKER),
        description: blank(f.get("Description")) ? "" : (f.get("Description") ?? "").trim(),
        offBoard: !flag(sym, "on_board", true),
      });
    }
    for (const sh of lists(t, "sheet")) {
      const f = fields(sh);
      const name = f.get("Sheetname") ?? f.get("Sheet name") ?? "";
      const file2 = f.get("Sheetfile") ?? f.get("Sheet file") ?? "";
      const target = file2 ? resolve(file, file2) : null;
      if (!target) {
        out.warnings.push(`The sheet ${name || "?"} (${file2 || "no file"}) isn't in the project: its parts are missing`);
        continue;
      }
      const uuid = str(first(sh, "uuid"));
      walk(
        target,
        `${path}/${uuid}`,
        `${legacyPath}/${uuid}`,
        { dnp: sheet.dnp || flag(sh, "dnp", false), inBom: sheet.inBom && flag(sh, "in_bom", true) },
        depth + 1,
      );
    }
  };
  walk(root, `/${str(first(tree(root), "uuid"))}`, "", { dnp: false, inBom: true }, 0);

  const groups = new Map<string, Part[]>();
  for (const p of parts.values()) {
    const key = [p.value, p.footprint, p.mpn, p.manufacturer, p.offBoard].join("\u0001");
    groups.set(key, [...(groups.get(key) ?? []), p]);
  }
  out.rows = [...groups.values()]
    .map((ps) => {
      const refs = ps.map((p) => p.ref).sort(refOrder);
      const p = ps[0];
      return {
        refs,
        qty: refs.length,
        value: p.value,
        footprint: p.footprint,
        mpn: p.mpn,
        manufacturer: p.manufacturer,
        description: p.description,
        offBoard: p.offBoard,
      };
    })
    .sort((a, b) => refOrder(a.refs[0], b.refs[0]));
  out.dnp = [...dnp].sort(refOrder);
  out.notInBom = [...notInBom].sort(refOrder);
  if (unannotated.size)
    out.warnings.push(
      `${[...unannotated].sort(refOrder).join(", ")} ${unannotated.size === 1 ? "isn't" : "aren't"} annotated: annotate the schematic in KiCad, then upload the project again`,
    );
  if (twice.size)
    out.warnings.push(
      `${[...twice].sort(refOrder).join(", ")} ${twice.size === 1 ? "is" : "are"} on two different parts: annotate the schematic again in KiCad`,
    );
  return out;
}

// --- The board and the BOM's lines ---

/** "C1, C2" or "R1-R4" (one prefix) → each reference. */
export function refsOf(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[,;\s]+/).filter(Boolean)) {
    const range = /^([A-Za-z_]+)(\d+)-\1?(\d+)$/.exec(part);
    if (range && Number(range[3]) >= Number(range[2]) && Number(range[3]) - Number(range[2]) < 1000) {
      for (let n = Number(range[2]); n <= Number(range[3]); n++) out.push(`${range[1]}${n}`);
    } else out.push(part);
  }
  return out;
}

/** A BOM line as the board tab reads it. */
export type BoardLine = { id: string; component_item_id: string | null; supply: string; ref_designator: string; qty: number | string };

/**
 * Each row's line (the first whose references take in any of the row's) and the row's references no line has; the
 * lines on no row (off the board: joysticks, the bag); and each line's references the board doesn't have.
 */
export function boardMatch(rows: readonly BoardRow[], lines: readonly BoardLine[]) {
  const byRef = new Map<string, BoardLine>();
  for (const l of lines) for (const ref of refsOf(l.ref_designator ?? "")) if (!byRef.has(ref.toUpperCase())) byRef.set(ref.toUpperCase(), l);
  const lineOf = rows.map((r) => r.refs.map((ref) => byRef.get(ref.toUpperCase())).find(Boolean) ?? null);
  const untied = rows.map((r, i) => (lineOf[i] ? r.refs.filter((ref) => byRef.get(ref.toUpperCase()) !== lineOf[i]) : []));
  const onBoard = new Set(rows.flatMap((r) => r.refs.map((ref) => ref.toUpperCase())));
  const used = new Set(lineOf.filter(Boolean).map((l) => (l as BoardLine).id));
  const gone = new Map<string, string[]>();
  for (const l of lines) {
    const missing = refsOf(l.ref_designator ?? "").filter((ref) => !onBoard.has(ref.toUpperCase()));
    if (missing.length && used.has(l.id)) gone.set(l.id, missing);
  }
  return { lineOf, untied, offBoard: lines.filter((l) => !used.has(l.id)), gone };
}

/** A line's references with some added or taken away, in board order ("U1, U2"). */
export function withRefs(current: string, add: readonly string[], remove: readonly string[] = []): string {
  const drop = new Set(remove.map((r) => r.toUpperCase()));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ref of [...refsOf(current), ...add]) {
    const k = ref.toUpperCase();
    if (drop.has(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(ref);
  }
  return out.sort(refOrder).join(", ");
}

const CSV = {
  en: {
    head: ["References", "Qty", "Value", "Footprint", "Manufacturer", "Part number", "Supplied by", "HHL part"],
    hhl: "Hand Held Legend (sent to you)",
    cm: (cm: string) => cm,
    dnp: "Not fitted (DNP)",
    offBoard: "not on the board",
  },
  zh: {
    head: ["位号", "数量", "值", "封装", "制造商", "料号", "供应方", "HHL 料号"],
    hhl: "Hand Held Legend 提供",
    cm: (cm: string) => `${cm} 采购`,
    dnp: "不贴 (DNP)",
    offBoard: "不在板上",
  },
};

/** The board's BOM for the CM: each row, who supplies it (HHL for a line HHL sends, else the CM), and the DNP ones. */
export function boardBomCsv(
  board: Pick<BoardBom, "rows" | "dnp">,
  lines: readonly BoardLine[],
  opts: { cm: string; skuOf: (line: BoardLine) => string; lang?: "en" | "zh" },
): string {
  const t = CSV[opts.lang ?? "en"];
  const { lineOf } = boardMatch(board.rows, lines);
  const esc = (s: string | number) => (/[",\r\n]/.test(String(s)) ? `"${String(s).replaceAll('"', '""')}"` : String(s));
  const out: (string | number)[][] = [t.head];
  board.rows.forEach((r, i) => {
    const line = lineOf[i];
    const hhl = line?.supply === "hhl_consigns";
    const value = r.offBoard ? `${r.value} (${t.offBoard})` : r.value;
    out.push([r.refs.join(", "), r.qty, value, r.footprint, r.manufacturer, r.mpn, hhl ? t.hhl : t.cm(opts.cm), hhl && line ? opts.skuOf(line) : ""]);
  });
  if (board.dnp.length) out.push([board.dnp.join(", "), board.dnp.length, "", "", "", "", t.dnp, ""]);
  return `﻿${out.map((r) => r.map(esc).join(",")).join("\r\n")}\r\n`;
}
